/**
 * VRG-573 — the canonical PRODUCT TYPE master list, moved here from
 * `apps/platform/src/constants/tagCatalog/product-types.ts` (VRG-141 gap-0) so BOTH apps
 * and the API read one copy.
 *
 * ⚠️ VRG-628 — this file and every symbol in it used to carry the OLD word for these.
 * The codebase had already moved on without them: the map is `PRODUCT_TYPE_CATEGORIES`,
 * the accessor is `categoriesForProductType()`, and the emitted field is
 * `best_for_product_types`. Only the constants holding the list still used the old word,
 * so one concept had two names depending on which file you opened. They are PRODUCT
 * TYPES now, everywhere, and their 14 containers are SECTORS.
 *
 * ⚠️ And they are NOT "industries". An industry is a WEBSITE marketing page, and an
 * industry maps to a product CATEGORY (`product-categories.ts`), not to a sector here.
 * Naming this layer "industry" would attach a website word to the wrong grain.
 *
 * **Why it moved.** It was platform-only, and three things now need it server-side:
 *
 *   1. `companies.categories` already stores these exact strings, written by the platform.
 *   2. Market-data extraction must constrain a claim's `category` to a CLOSED set. Free
 *      text is what made Ask's category filter misfire — a question about "resale" never
 *      matched a claim the model had filed under "Authentication", because nothing forced
 *      the two to be drawn from the same vocabulary.
 *   3. Outreach (VRG-539) wants "content in this brand's product area", which is a JOIN
 *      between a claim's category and a company's categories. That join only exists if
 *      both sides share one list — which is the whole argument for hoisting it to
 *      layer 1 rather than copying it into the API (working-conventions §36, §47).
 *
 * The platform's own constants file now re-exports from here, so its ~20 call sites are
 * untouched and there is still exactly one definition.
 *
 * Source of truth for the CONTENT: supply/tag-shop-catalog.md §best_for (2026-07-22).
 * (that doc's heading keeps the old spelling; the FIELD is `best_for_product_types`.)
 * `best_for_product_types` (product type — "what it's for") is a SEPARATE axis from capability
 * (Connect/Verify/…) and from `MetricSolution` (which Vera product area applies).
 *
 * The 14 SECTORS double as the shopfront filter/nav hierarchy.
 *
 * 2026-08-06: "Documents & Credentials" split into TWO product types. They are different
 * buyers with different forms: a *document* is paper being authenticated (certificates,
 * deeds, provenance letters), whereas a *credential* is an access token carried by a
 * person — which is the Card Tag's whole use case, and shares its icon with Memberships
 * because it is the same job. Bundling them hid the Card/Sticker credential use case
 * behind a filing-cabinet label.
 *
 * 2026-08-06: "Footwear (non-sneaker)" → "Footwear". The parenthetical existed only to
 * disambiguate from Sneakers, which is already its own product type alongside it, so it
 * was carrying no information — while the parens defeated the icon normalizer (which
 * strips only whitespace and "&") AND made the chip wide enough to fill a whole row in
 * the shop card's two-row zone.
 */

export type ProductSector = {
  /** nav/section label. */
  sector: string;
  /** product types in this sector (canonical casing). */
  types: string[];
  /** regulated/controlled — needs a compliance flag before actively marketing. */
  regulated?: boolean;
};

export const PRODUCT_SECTORS: ProductSector[] = [
  {
    sector: 'Fashion & Apparel',
    types: [
      'Apparel & Clothing',
      'Sneakers',
      'Footwear',
      'Handbags',
      'Wallets',
      'Luggage',
      'Eyewear',
      'Belts',
      'Scarves',
      'Hats',
    ],
  },
  {
    sector: 'Jewelry, Watches & Precious',
    types: ['Jewelry', 'Watches', 'Gemstones', 'Bullion'],
  },
  {
    sector: 'Beauty & Personal Care',
    types: ['Fragrance & Perfume', 'Cosmetics', 'Skincare', 'Candles'],
  },
  {sector: 'Art & Design', types: ['Art', 'Sculpture', 'Photography']},
  // VRG-152 — Coins split out from Currency (numismatic vs everyday currency).
  {
    sector: 'Collectibles',
    types: [
      'Trading Cards',
      'Currency',
      'Coins',
      'Stamps',
      'Comics',
      'Memorabilia',
      'Toys',
      'Collectibles',
    ],
  },
  {sector: 'Music, Media & Books', types: ['Vinyl & Records', 'Musical Instruments', 'Books']},
  // VRG-152 — Caviar added (perishable, Verify + Temperature candidate).
  {
    sector: 'Wine, Spirits & Gourmet',
    types: ['Wine', 'Spirits', 'Gourmet Food', 'Coffee & Tea', 'Cigars & Tobacco', 'Caviar'],
  },
  {
    sector: 'Automotive & Transport',
    types: [
      'Automobiles',
      'Auto Parts',
      'Motorcycles',
      'Bicycles',
      'Marine & Boats',
    ],
  },
  {sector: 'Electronics & Tech', types: ['Electronics', 'Audio Equipment']},
  {sector: 'Home & Living', types: ['Furniture', 'Home Décor', 'Rugs & Carpets', 'Lighting']},
  {
    sector: 'Sports & Outdoor',
    types: ['Sporting Goods', 'Golf Equipment', 'Camping'],
  },
  // VRG-152 — Hazardous added (regulated, Verify + Tamper / Verify + Trace).
  {
    sector: 'Regulated & Controlled',
    regulated: true,
    types: [
      'Firearms',
      'Cannabis',
      'Pharmaceuticals',
      'Medical Devices',
      'Supplements',
      'Hazardous',
    ],
  },
  {
    sector: 'Industrial & B2B',
    types: ['Industrial Machinery', 'Aerospace & Defense', 'Electrical Components'],
  },
  {
    sector: 'Access & Digital',
    types: ['Memberships', 'Ticketing & Access', 'Documents', 'Credentials'],
  },
  {sector: 'Luxury Miscellany', types: ['Writing Instruments', 'Lighters']},
  {sector: 'Other', types: ['Other / Not Listed']},
];

/** Flat list of every canonical product type (for validation / autocomplete / model enums). */
export const ALL_PRODUCT_TYPES: string[] = PRODUCT_SECTORS.flatMap(g => g.types);

/** Product types requiring a compliance/liability review before active marketing. */
export const REGULATED_PRODUCT_TYPES: Set<string> = new Set(
  PRODUCT_SECTORS.filter(g => g.regulated).flatMap(g => g.types),
);

/**
 * Case-insensitive canonicaliser. Returns the canonical casing for a product type, or `null`
 * if the input is not one.
 *
 * Exists because the two producers of these strings have different failure modes: a human
 * typing into the platform picks from a list and cannot get the casing wrong, whereas a
 * language model asked to choose from an enum reliably returns `'handbags & purses'` or
 * `'Handbags and Purses'`. Rejecting those outright would throw away a correct answer over
 * presentation; accepting them raw would put two spellings of one product type in the column
 * and silently break the company↔content join this list was hoisted to enable.
 *
 * Deliberately NOT fuzzy. `'&'`/`'and'` and whitespace/case are normalised; nothing else
 * is. A near-miss returns null and the caller drops the value, because a mis-filed
 * product type is worse than an absent one — it makes the wrong content reachable from a
 * brand's outreach queue.
 */
const TYPE_BY_KEY = new Map<string, string>(
  ALL_PRODUCT_TYPES.map(v => [v.toLowerCase().replace(/\band\b/g, '&').replace(/\s+/g, ' ').trim(), v]),
);

export const canonicalProductType = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const key = raw.toLowerCase().replace(/\band\b/g, '&').replace(/\s+/g, ' ').trim();
  return TYPE_BY_KEY.get(key) ?? null;
};


/* ═══════════════════════════════════════════════════════════════════════════
   PRODUCT-TYPE CODES — the short form used in tag-imagery filenames.

   Spencer, 2026-09-18: a photograph of a tag APPLIED to something should record
   what it is applied to, so an industry page can ask for "everything shot on
   wine & spirits" and the tag shop can filter the same library.

   ⚠️ "PRODUCT TYPE" EVERYWHERE, IDENTIFIERS INCLUDED. Spencer, 2026-09-18:
   "that's sort of what it is." A photograph shows a bottle, not a market
   segment. This note used to end by conceding that the identifiers in this file
   still said otherwise, because renaming them was "its own migration" — VRG-628
   is that migration, so the concession is spent and the whole file agrees with
   the filename token and the docs.

   ── ⚠️ WHY CODES AND NOT SLUGS ────────────────────────────────────────────
   These names carry spaces and ampersands — `Apparel & Clothing`,
   `Handbags & Purses`. Slugged they become `apparel-clothing`, and that HYPHEN
   breaks the filename grammar, which splits on `-` to recover form, size,
   options, view and sequence. A fixed code cannot break it, and it matches what
   the SKU vocabulary already does everywhere else (STK, FPT, VER).

   ── ⚠️ WHY LEAF PRODUCT TYPES AND NOT THE 12 WEBSITE INDUSTRIES ───────────
   The website groups `wines-and-spirits` as ONE industry; this list has `Wine`
   and `Spirits` as two, and the enclosing SECTOR also sweeps in Gourmet Food,
   Coffee & Tea, Cigars & Tobacco and Caviar. None of the three coincide.

   A photograph of a tag on a whisky bottle IS Spirits — a fact about the
   picture. `wines-and-spirits` is a grouping DECISION, and a filename is the
   worst place to bake in a decision that may be revisited. Fine grain groups
   upward at query time; coarse grain cannot be split without renaming the
   library.
   ═══════════════════════════════════════════════════════════════════════════ */
export const PRODUCT_TYPE_CODE: Record<string, string> = {
  "Apparel & Clothing": 'APPA',
  "Sneakers": 'SNEA',
  "Footwear": 'FOOT',
  "Handbags": 'HAND',
  "Wallets": 'WALL',
  "Luggage": 'LUGG',
  "Eyewear": 'EYEW',
  "Belts": 'BELT',
  "Scarves": 'SCAR',
  "Hats": 'HATS',
  "Jewelry": 'JEWE',
  "Watches": 'WATC',
  "Gemstones": 'LOOS',
  "Bullion": 'BULL',
  "Fragrance & Perfume": 'FRAG',
  "Cosmetics": 'COSM',
  "Skincare": 'SKIN',
  "Candles": 'CAND',
  "Art": 'ART',
  "Sculpture": 'SCUL',
  "Photography": 'PHOT',
  "Trading Cards": 'TRAD',
  "Currency": 'CURR',
  "Coins": 'COIN',
  "Stamps": 'STAM',
  "Comics": 'COMI',
  "Memorabilia": 'MEMO',
  "Toys": 'TOYS',
  "Collectibles": 'COLL',
  "Vinyl & Records": 'VINY',
  "Musical Instruments": 'MUSI',
  "Books": 'BOOK',
  "Wine": 'WINE',
  "Spirits": 'SPIR',
  "Gourmet Food": 'GOUR',
  "Coffee & Tea": 'COFF',
  "Cigars & Tobacco": 'CIGA',
  "Caviar": 'CAVI',
  "Automobiles": 'AUTO',
  "Auto Parts": 'AUTP',
  "Motorcycles": 'MOTO',
  "Bicycles": 'BICY',
  "Marine & Boats": 'MARI',
  "Electronics": 'ELEC',
  "Audio Equipment": 'AUDI',
  "Furniture": 'FURN',
  "Home D\u00e9cor": 'HOME',
  "Rugs & Carpets": 'RUGS',
  "Lighting": 'LIGH',
  "Sporting Goods": 'SPOR',
  "Golf Equipment": 'GOLF',
  "Camping": 'CAMP',
  "Firearms": 'FIRE',
  "Cannabis": 'CANN',
  "Pharmaceuticals": 'PHAR',
  "Medical Devices": 'MEDI',
  "Supplements": 'SUPP',
  "Hazardous": 'HAZA',
  "Industrial Machinery": 'INDU',
  "Aerospace & Defense": 'AERO',
  "Electrical Components": 'ELCO',
  "Memberships": 'MEMB',
  "Ticketing & Access": 'TICK',
  "Documents": 'DOCU',
  "Credentials": 'CRED',
  "Writing Instruments": 'WRIT',
  "Lighters": 'LIGA',
  "Other / Not Listed": 'OTHE',
};

/** Code → product type. Derived, so the two directions cannot disagree. */
export const PRODUCT_TYPE_BY_CODE: Record<string, string> = Object.fromEntries(
  Object.entries(PRODUCT_TYPE_CODE).map(([name, code]) => [code, name]),
);

/* ⚠️ A MODULE-LOAD GUARD, NOT A TEST, because what it catches is silent.
   A product type added to PRODUCT_SECTORS with no code here is simply
   un-taggable: every photo of it falls back to an untagged name and the
   industry page returns fewer results, which reads as "we have no photos of
   that" rather than as a bug. A leftover code resolves to a name nothing else
   knows. And two types sharing a code makes a filename ambiguous — filenames
   are the only record here, so that is unrecoverable.

   This guard is also how the list was built: a hand-written regex over the
   sectors in this file silently produced 62 of 68, dropping Firearms,
   Pharmaceuticals, Medical Devices, Supplements, Cannabis and Hazardous. */
{
  const missing = ALL_PRODUCT_TYPES.filter((v) => !PRODUCT_TYPE_CODE[v]);
  const extra = Object.keys(PRODUCT_TYPE_CODE).filter((v) => !ALL_PRODUCT_TYPES.includes(v));
  const codes = Object.values(PRODUCT_TYPE_CODE);
  const dupes = [...new Set(codes.filter((c, i) => codes.indexOf(c) !== i))];
  const problems = [
    missing.length ? `no code for: ${missing.join(', ')}` : '',
    extra.length ? `code for unknown type: ${extra.join(', ')}` : '',
    dupes.length ? `duplicate codes: ${dupes.join(', ')}` : '',
  ].filter(Boolean);
  if (problems.length) {
    throw new Error(
      `PRODUCT_TYPE_CODE is out of sync with PRODUCT_SECTORS — ${problems.join('; ')}`,
    );
  }
}
